import React from "react";
import { Card, Tabs, Typography, Space, Row, Col, Table } from "antd";

const { Title, Paragraph, Text } = Typography;

const AIApproaches = () => {
  const renderSearch = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={<span style={{ color: "#0071e3" }}>What is AI as Search?</span>}
      >
        <Paragraph style={{ fontSize: "16px" }}>
          Imagine you're trying to solve a maze. Search-based AI works by
          exploring different paths until it finds the best way out. It looks at
          all the possible routes, figures out which ones work, and picks the
          shortest or easiest one.
        </Paragraph>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>Cool Example: Chess Computer</span>
        }
      >
        <Paragraph style={{ marginBottom: 0 }}>
          When you play chess against a computer, it thinks ahead by imagining
          millions of possible moves and choosing the one most likely to win.
          It's like the computer is looking into the future to see what might
          happen!
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>How Does It Work?</span>}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Text style={{ paddingTop: "4px" }}>
            1. First, understand what problem needs to be solved (like winning a
            chess game)
          </Text>
          <Text style={{ paddingTop: "4px" }}>
            2. Think of all the different ways to solve it (all possible moves)
          </Text>
          <Text style={{ paddingTop: "4px" }}>
            3. Check each option to see which one works best
          </Text>
          <Text style={{ paddingTop: "4px" }}>
            4. Pick the winner and do it!
          </Text>
        </Space>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            Where You See This in Real Life
          </span>
        }
      >
        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li>GPS apps finding the fastest route to your destination</li>
          <li>Video game characters that seem smart and make good decisions</li>
          <li>Delivery companies planning the best routes for their trucks</li>
          <li>Robots moving around without bumping into things</li>
          <li>Puzzle-solving apps that can beat any level</li>
        </ul>
      </Card>
    </Space>
  );

  const renderLearning = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>What is AI as Learning?</span>
        }
      >
        <Paragraph style={{ fontSize: "16px" }}>
          Think about how you learned to recognize your friends' faces or
          understand what different emojis mean. Learning-based AI works the
          same way - it looks at thousands of examples and learns to recognize
          patterns, just like you do when you practice something over and over.
        </Paragraph>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            Cool Example: Netflix Recommendations
          </span>
        }
      >
        <Paragraph style={{ marginBottom: 0 }}>
          Netflix watches what millions of people enjoy and learns patterns. If
          people who loved Stranger Things also loved Wednesday, it might
          recommend Wednesday to you too. It learns what goes together by seeing
          what people actually watch!
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>How Does It Work?</span>}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Text style={{ paddingTop: "4px" }}>
            1. Collect lots of examples (like thousands of pictures of cats and
            dogs)
          </Text>
          <Text style={{ paddingTop: "4px" }}>
            2. Let the AI practice recognizing patterns in those examples
          </Text>
          <Text style={{ paddingTop: "4px" }}>
            3. Check if it's learning correctly and help it get better
          </Text>
          <Text style={{ paddingTop: "4px" }}>
            4. Keep practicing until it gets really good at its job
          </Text>
        </Space>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            Where You See This in Real Life
          </span>
        }
      >
        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li>Face ID on your phone recognizing your face</li>
          <li>
            Spotify suggesting songs you might like based on what you listen to
          </li>
          <li>Email apps catching spam messages automatically</li>
          <li>Voice assistants like Siri understanding what you say</li>
          <li>Apps that can translate between different languages</li>
        </ul>
      </Card>
    </Space>
  );

  const renderHybrid = () => (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card
        title={
          <span style={{ color: "#0071e3" }}>What are Hybrid Systems?</span>
        }
      >
        <Paragraph style={{ fontSize: "16px" }}>
          Imagine having a friend who's both really good at planning ahead AND
          quick at learning from experience. Hybrid AI combines the 'searching'
          and 'learning' approaches together, using the strengths of both to
          solve really hard problems.
        </Paragraph>
      </Card>

      <Card
        title={<span style={{ color: "#0071e3" }}>Cool Example: AlphaGo</span>}
      >
        <Paragraph style={{ marginBottom: 0 }}>
          AlphaGo defeated the world's best Go players by combining deep neural
          networks for position evaluation with Monte Carlo tree search for
          strategic planning. It beat Lee Sedol (a top player) in 2016 - a huge
          milestone in AI history!
        </Paragraph>
      </Card>

      <Card title={<span style={{ color: "#0071e3" }}>How Does It Work?</span>}>
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Text style={{ paddingTop: "4px" }}>
            1. Use learning to understand patterns and make smart guesses
          </Text>
          <Text style={{ paddingTop: "4px" }}>
            2. Use searching to plan ahead and explore options
          </Text>
          <Text style={{ paddingTop: "4px" }}>
            3. Combine both strengths to handle complex situations
          </Text>
          <Text style={{ paddingTop: "4px" }}>
            4. Keep improving all parts of the system together
          </Text>
        </Space>
      </Card>

      <Card
        title={
          <span style={{ color: "#0071e3" }}>
            Where You See This in Real Life
          </span>
        }
      >
        <ul style={{ paddingLeft: "20px", marginBottom: 0 }}>
          <li>
            Self-driving cars that see the road (learning) and plan safe routes
            (searching)
          </li>
          <li>
            Smart game opponents that learn your style and plan strategies
          </li>
          <li>Medical tools that recognize diseases and suggest treatments</li>
          <li>
            Robot helpers that understand their environment and plan their
            actions
          </li>
          <li>
            Advanced assistants that understand language and solve problems
          </li>
        </ul>
      </Card>
    </Space>
  );

  const renderComparison = () => {
  const approachesData = [
    {
      key: "1",
      approach: "AI as Search",
      description: "Finding the best answer by trying different options",
      bestFor: "Best when you need the perfect answer and know all the rules",
      example: "Chess computers, GPS navigation, puzzle solvers",
      strengths: [
        "Always finds best answer",
        "Explainable decisions",
        "No training needed",
        "Predictable"
      ],
      limitations: [
        "Slow when there are many options",
        "Needs clear rules",
        "Can't learn, always fixed"
      ]
    },
    {
      key: "2",
      approach: "AI as Learning",
      description: "Getting smarter by practicing with examples",
      bestFor: "Best when there are patterns to discover in lots of examples",
      example: "Face recognition, Netflix recommendations, spam filters",
      strengths: [
        "Handles complex patterns",
        "Gets better over time",
        "Works with messy data",
        "Finds connections"
      ],
      limitations: [
        "Needs so much data to learn",
        "Hard to explain the outcomes",
        "Can copy bad patterns"
      ]
    },
    {
      key: "3",
      approach: "Hybrid Systems",
      description: "Combining different AI approaches for the best results",
      bestFor: "Best for really hard problems that need multiple approaches",
      example: "AlphaGo, self-driving cars, advanced medical tools",
      strengths: [
        "Combines best parts",
        "Handles complexity",
        "More reliable",
        "Most powerful"
      ],
      limitations: [
        "Expensive to build",
        "Needs many experts to build",
        "High compute power to run"
      ]
    }
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title={<span style={{ color: "#0071e3" }}>Quick Comparison</span>}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    borderBottom: "2px solid #0071e3",
                    width: "25%",
                  }}
                >
                  <Text strong>Approach</Text>
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    width: "25%",
                    borderBottom: "2px solid #0071e3",
                  }}
                >
                  <Text strong>Description</Text>
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    borderBottom: "2px solid #0071e3",
                  }}
                >
                  <Text strong>Best For</Text>
                </th>
                <th
                  style={{
                    padding: "12px 16px",
                    textAlign: "left",
                    borderBottom: "2px solid #0071e3",
                  }}
                >
                  <Text strong>Example</Text>
                </th>
              </tr>
            </thead>
            <tbody>
              {approachesData.map((row, idx) => (
                <tr
                  key={row.key}
                  style={{
                    borderBottom:
                      idx < approachesData.length - 1
                        ? "2px solid #f0f0f0"
                        : "none",
                  }}
                >
                  <td
                    style={{
                      padding: "16px",
                      verticalAlign: "top",
                      textAlign: "left",
                    }}
                  >
                    <Text strong style={{ color: "#0071e3" }}>
                      {row.approach}
                    </Text>
                  </td>
                  <td
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      verticalAlign: "top",
                    }}
                  >
                    {row.description}
                  </td>
                  <td
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      verticalAlign: "top",
                    }}
                  >
                    {row.bestFor}
                  </td>
                  <td
                    style={{
                      padding: "16px",
                      textAlign: "left",
                      verticalAlign: "top",
                    }}
                  >
                    {row.example}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Row gutter={[16, 16]}>
        {approachesData.map((data) => (
          <Col xs={24} md={8} key={data.key}>
            <Card
              title={<span style={{ color: "#0071e3" }}>{data.approach}</span>}
            >
              <Paragraph>
                <Text strong>Strengths:</Text>
              </Paragraph>
              <ul style={{ paddingLeft: "20px", fontSize: "14px" }}>
                {data.strengths.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
              <Paragraph>
                <Text strong>Limitations:</Text>
              </Paragraph>
              <ul
                style={{
                  paddingLeft: "20px",
                  fontSize: "14px",
                  marginBottom: 0,
                }}
              >
                {data.limitations.map((item, idx) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </Card>
          </Col>
        ))}
      </Row>
    </Space>
  );
};

  const tabItems = [
    {
      key: "search",
      label: "AI as Search",
      children: renderSearch(),
    },
    {
      key: "learning",
      label: "AI as Learning",
      children: renderLearning(),
    },
    {
      key: "hybrid",
      label: "Hybrid Systems",
      children: renderHybrid(),
    },
    {
      key: "comparison",
      label: "Comparison",
      children: renderComparison(),
    },
  ];

  return (
    <div className="page-container">
      <header
        style={{ textAlign: "center", marginBottom: "32px" }}
        className="fade-in-down"
      >
        <Title level={1}>Three Ways AI Works</Title>
        <Paragraph style={{ fontSize: "18px" }}>
          Learn how computers can be smart in different ways
        </Paragraph>
      </header>

      <Tabs defaultActiveKey="search" items={tabItems} size="large" />
    </div>
  );
};

export default AIApproaches;
